import { type ReactNode } from 'react';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import defaultImage from '@/assets/images/png/default-path-image.png';
import {
  ResourceCard,
  ResourceCardContainer,
  ResourceCardContent,
  ResourceCardHeader,
  ResourceCardSubtitle,
  ResourceCardTitle,
} from '@/components/shared/resource-card';
import { useTrackItemClick } from '@/context/analytics-item';
import type { AnalyticsItem } from '@/context/analytics-item/interfaces';
import { lineClamp } from '@/utils/line-clamp';

export type LearningTypeCardProps = {
  description: string;
  image: string;
  label: string;
  link?: string;
  customAction?: ReactNode;
  info?: string;
  onClick?: () => void;
  analyticsItem?: AnalyticsItem;
};

export const LearningTypeCard = ({
  label,
  image,
  description,
  link,
  customAction,
  info,
  onClick,
  analyticsItem,
}: LearningTypeCardProps) => {
  const navigate = useNavigate();
  const trackClick = useTrackItemClick(analyticsItem);

  const handleClick = () => {
    trackClick();

    if (onClick) onClick();
    else if (link) navigate(link);
  };

  return (
    <ResourceCard onClick={handleClick} href={link ?? undefined}>
      <ResourceCardHeader
        image={{ src: image ?? defaultImage, alt: '', type: 'image' }}
      />
      <ResourceCardContainer>
        <ResourceCardContent>
          <ResourceCardTitle lines={2}>{label}</ResourceCardTitle>
          {info && <ResourceCardSubtitle>{info}</ResourceCardSubtitle>}
          <Typography sx={lineClamp(3)}>{description}</Typography>
        </ResourceCardContent>
        {customAction}
      </ResourceCardContainer>
    </ResourceCard>
  );
};
